import { useState, useCallback, useRef, useEffect } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { acquireApiToken } from '@/auth/acquireApiToken';
import { Env, getAdminApiUrl } from "../../shared";

export interface Notification {
  id: string;
  businessId: string;
  businessName: string;
  businessLogo: string | null;
  title: string;
  body: string;
  visibility: string;
  subscriberCount: number;
  sentCount: number;
  deliveredCount: number;
  openedCount: number;
  status: string;
  createdAt: string;
}

export interface UseAllNotificationsResult {
  notifications: Notification[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useAllNotifications({
  env,
  search,
  limit,
  offset,
}: {
  env: Env;
  search: string;
  limit: number;
  offset: number;
}): UseAllNotificationsResult {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    const apiUrl = getAdminApiUrl(env);
    if (!apiUrl) {
      setError("API URL is not configured for this environment");
      return;
    }

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        let token: string | null = null;
        if (isAuthenticated && accountsRef.current.length > 0) {
          token = await acquireApiToken(instance, accountsRef.current[0]);
        }

        const params = new URLSearchParams({
          limit: String(limit),
          offset: String(offset),
        });
        if (search) params.set("search", search);

        const response = await fetch(`${apiUrl}/admin/notifications?${params}`, {
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });

        const data = await response.json();
        if (!response.ok) {
          if (!cancelled) setError(data.error || `HTTP ${response.status}`);
          return;
        }

        if (!cancelled) {
          setNotifications(data.notifications ?? []);
          setTotal(data.total ?? 0);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message ?? "Unknown error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [env, search, limit, offset, tick, isAuthenticated, instance]);

  return { notifications, total, loading, error, refetch };
}
